import { useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { create } from 'zustand';

import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { StudioFeatureFlagsContext } from '../../../hooks/useStudioFeatureFlagsContext';
import { useAssistantStore } from '../../../store/assistants/assistantsStore';

export type NodePosition = { x: number; y: number; isFixed: boolean };

type GraphStore = {
  nodePositions: Record<string, NodePosition>;
  setNodePositions: (nodePositions: Record<string, NodePosition>) => void;
  graphVisible: boolean;
  setGraphVisible: (graphVisible: boolean) => void;
  fitToViewMode: boolean;
  setFitToViewMode: (fitToViewMode: boolean) => void;
  needsFitting: boolean;
  setNeedsFitting: (needsFitting: boolean) => void;
};

export const useGraphStore = create<GraphStore>((set) => {
  return {
    nodePositions: {},
    setNodePositions: (nodePositions) => set({ nodePositions }),
    graphVisible: false,
    setGraphVisible: (graphVisible) => set({ graphVisible }),
    fitToViewMode: false,
    setFitToViewMode: (fitToViewMode) => set({ fitToViewMode }),
    needsFitting: false,
    setNeedsFitting: (needsFitting) => set({ needsFitting }),
  };
});

export const useSaveNodePositions = () => {
  const { showGraphZoomControls } = useContext(StudioFeatureFlagsContext);
  const assistantId = useAssistantStore((state) => state.activeAssistantId);
  const [searchParams] = useSearchParams();
  const baseUrl = searchParams.get('baseUrl') ?? undefined;
  const hostProjectId = searchParams.get('hostProjectId') ?? undefined;

  const nodePositions = useGraphStore((state) => state.nodePositions);
  const _setNodePositions = useGraphStore((state) => state.setNodePositions);

  const [nodePositionsLocalStorage, setNodePositionsLocalStorage] =
    useLocalStorageState(
      `ls:studio:${baseUrl}:${hostProjectId}:${assistantId}:nodePosition`,
      {},
      z.record(
        z.string(),
        z.object({ x: z.number(), y: z.number(), isFixed: z.boolean() })
      )
    );

  // Load node positions from local storage
  useEffect(() => {
    if (!showGraphZoomControls) return;
    _setNodePositions(nodePositionsLocalStorage);
  }, [nodePositionsLocalStorage, _setNodePositions, showGraphZoomControls]);

  // Save node positions to local storage
  useEffect(() => {
    if (!showGraphZoomControls) return;
    setNodePositionsLocalStorage(nodePositions);
  }, [nodePositions, setNodePositionsLocalStorage, showGraphZoomControls]);
};

export const useSaveFitToViewMode = () => {
  const fitToViewMode = useGraphStore((state) => state.fitToViewMode);
  const setFitToViewMode = useGraphStore((state) => state.setFitToViewMode);
  const [fitToViewModeLocalStorage, setFitToViewModeLocalStorage] =
    useLocalStorageState(`ls:studio:fitToViewMode`, false, z.boolean());

  // Load fit to view mode from local storage
  useEffect(() => {
    setFitToViewMode(fitToViewModeLocalStorage);
  }, [fitToViewModeLocalStorage, setFitToViewMode]);

  // Save fit to view mode to local storage
  useEffect(() => {
    setFitToViewModeLocalStorage(fitToViewMode);
  }, [fitToViewMode, setFitToViewModeLocalStorage]);
};
